import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import InquiryForm, { type InquiryVehicleType } from "./InquiryForm";
import { trackEvent, trackInquirySheetClose, type InquirySheetCloseReason } from "@/lib/analytics";
import {
  getInquirySheetTelemetry,
  resetInquirySheetTelemetry,
} from "@/lib/inquirySheetTelemetry";

interface InquirySheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  vehicleType: InquiryVehicleType;
  vehicleLabel: string;
  vehicleImage?: string;
  vehicleId?: string;
}

const InquirySheet = ({ open, onOpenChange, vehicleType, vehicleLabel, vehicleImage, vehicleId }: InquirySheetProps) => {
  const { t } = useTranslation();
  const openedAtRef = useRef<number | null>(null);
  const viewportHRef = useRef<number>(0);
  const closeReasonRef = useRef<InquirySheetCloseReason>("unknown");
  const submittedRef = useRef(false);
  const forceCloseRef = useRef(false);
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);

  // Track dialog open for funnel analytics
  useEffect(() => {
    if (open) {
      resetInquirySheetTelemetry();
      openedAtRef.current = Date.now();
      viewportHRef.current = typeof window !== "undefined" ? window.innerHeight : 0;
      closeReasonRef.current = "unknown";
      submittedRef.current = false;
      trackEvent({
        eventType: "inquiry_sheet_open",
        eventData: {
          vehicle_type: vehicleType,
          vehicle_label: vehicleLabel,
          vehicle_id: vehicleId ?? null,
          viewport_h: viewportHRef.current,
        },
      });
    }
  }, [open, vehicleType, vehicleLabel, vehicleId]);

  // Listen for close signals (Escape, X-button, backdrop pointer-down) while open.
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeReasonRef.current = "escape";
    };
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      // Radix Dialog overlay carries role=dialog wrapper; the overlay itself is the [data-state=open] sibling.
      if (target.closest("[data-radix-dialog-content]")) {
        // Clicks inside content – the X-button has aria-label "Close" in the default DialogContent.
        const closeBtn = target.closest('button[aria-label="Close"]');
        if (closeBtn) closeReasonRef.current = "x_button";
      } else {
        // Outside content → backdrop click
        closeReasonRef.current = "backdrop";
      }
    };

    // Tab/Window schließt oder wechselt: als eigenständigen Close mit Grund loggen,
    // weil Radix in dem Fall kein onOpenChange(false) mehr feuert.
    let unloadLogged = false;
    const logImplicitClose = (reason: InquirySheetCloseReason) => {
      if (unloadLogged || submittedRef.current || openedAtRef.current == null) return;
      unloadLogged = true;
      const tele = getInquirySheetTelemetry();
      trackInquirySheetClose({
        vehicle_type: vehicleType,
        vehicle_label: vehicleLabel,
        vehicle_id: vehicleId ?? null,
        time_in_sheet_seconds: Math.round((Date.now() - (openedAtRef.current ?? Date.now())) / 1000),
        had_field_focus: tele.hadFieldFocus,
        max_scroll_depth_in_sheet: tele.maxScrollDepth,
        close_reason: reason,
        first_field_focused: tele.firstFieldFocused,
        viewport_h: viewportHRef.current,
      });
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") logImplicitClose("tab_hidden");
    };
    const onPageHide = () => logImplicitClose("page_unload");
    const onBeforeUnload = () => logImplicitClose("page_unload");

    window.addEventListener("keydown", onKeyDown, true);
    window.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("keydown", onKeyDown, true);
      window.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [open, vehicleType, vehicleLabel, vehicleId]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && openedAtRef.current != null) {
      const tele = getInquirySheetTelemetry();
      const reason: InquirySheetCloseReason = submittedRef.current
        ? "submitted"
        : closeReasonRef.current;

      if (tele.hadFieldFocus && !submittedRef.current && !forceCloseRef.current) {
        setCloseConfirmOpen(true);
        return;
      }

      trackInquirySheetClose({
        vehicle_type: vehicleType,
        vehicle_label: vehicleLabel,
        vehicle_id: vehicleId ?? null,
        time_in_sheet_seconds: Math.round((Date.now() - openedAtRef.current) / 1000),
        had_field_focus: tele.hadFieldFocus,
        max_scroll_depth_in_sheet: tele.maxScrollDepth,
        close_reason: reason,
        first_field_focused: tele.firstFieldFocused,
        viewport_h: viewportHRef.current,
      });
      openedAtRef.current = null;
      forceCloseRef.current = false;
    }
    onOpenChange(nextOpen);
  };

  return (
    <>
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="w-[95vw] max-w-5xl sm:max-w-5xl !max-w-[95vw] sm:!max-w-5xl min-w-0 p-0 gap-0 overflow-hidden max-h-[95dvh] [&>*]:min-w-0 [&>*]:max-w-full max-md:!left-0 max-md:!top-0 max-md:!translate-x-0 max-md:!translate-y-0 max-md:!w-screen max-md:!max-w-none max-md:!h-[100dvh] max-md:!max-h-[100dvh] max-md:!rounded-none max-md:!border-0 max-md:overflow-y-auto max-md:overscroll-contain"
      >
        <VisuallyHidden>
          <DialogTitle>{t("inquiry.sheet.title", { vehicle: vehicleLabel })}</DialogTitle>
          <DialogDescription>{t("inquiry.sheet.description", { vehicle: vehicleLabel })}</DialogDescription>
        </VisuallyHidden>
        {open && (
          <InquiryForm
            key={`${vehicleId ?? vehicleType}-${open}`}
            variant="dialog"
            vehicleType={vehicleType}
            vehicleLabel={vehicleLabel}
            vehicleImage={vehicleImage}
            vehicleId={vehicleId}
            source="inquiry_sheet"
            onSubmitted={() => { submittedRef.current = true; }}
            onClose={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
    <AlertDialog open={closeConfirmOpen} onOpenChange={setCloseConfirmOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t("inquiry.sheet.closeTitle")}</AlertDialogTitle>
          <AlertDialogDescription>{t("inquiry.sheet.closeDescription")}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t("inquiry.sheet.continueEditing")}</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => {
              forceCloseRef.current = true;
              setCloseConfirmOpen(false);
              handleOpenChange(false);
            }}
          >
            {t("inquiry.sheet.closeRequest")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
};

export default InquirySheet;
